import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Plus, Save, ArrowUp, ArrowDown, Eye, EyeOff, X, ChevronDown, ChevronRight } from "lucide-react";
import { api, assetUrl, formatApiError } from "@/lib/api";

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-lux block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Editor({ cfg, item, onSaved, onClose }) {
  const [d, setD] = useState(() => {
    const base = { vendors: [], photos: [], testimonials: [], ...item };
    if ((!base.testimonials || base.testimonials.length === 0) && item.testimonial_quote) {
      base.testimonials = [{ quote: item.testimonial_quote, author: item.testimonial_author || "", role: "" }];
    }
    return base;
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const r = await uploadFile(f);
        uploaded.push({ url: r.url, type: r.type || "image" });
      }
      setD((x) => ({ ...x, photos: [...(x.photos || []), ...uploaded] }));
      toast.success(`${uploaded.length} photo(s) added — remember to Save`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const setFeatured = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const r = await uploadFile(f); set("featured_image", r.url); toast.success("Featured image set — remember to Save"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const removePhoto = (i) => setD((x) => ({ ...x, photos: x.photos.filter((_, idx) => idx !== i) }));
  const movePhoto = (i, dir) => setD((x) => {
    const p = [...x.photos]; const j = i + dir; if (j < 0 || j >= p.length) return x;
    [p[i], p[j]] = [p[j], p[i]]; return { ...x, photos: p };
  });

  const addVendor = () => setD((x) => ({ ...x, vendors: [...(x.vendors || []), { name: "", role: "" }] }));
  const setVendor = (i, k, v) => setD((x) => ({ ...x, vendors: x.vendors.map((ve, idx) => idx === i ? { ...ve, [k]: v } : ve) }));
  const removeVendor = (i) => setD((x) => ({ ...x, vendors: x.vendors.filter((_, idx) => idx !== i) }));

  const addTestimonial = () => setD((x) => ({ ...x, testimonials: [...(x.testimonials || []), { quote: "", author: "", role: "" }] }));
  const setTestimonial = (i, k, v) => setD((x) => ({ ...x, testimonials: x.testimonials.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const removeTestimonial = (i) => setD((x) => ({ ...x, testimonials: x.testimonials.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        title: d.title || "", date: d.date || "", venue: d.venue || "", city: d.city || "",
        description: d.description || "", preview: d.preview || "",
        testimonials: (d.testimonials || []).filter((t) => t.quote),
        testimonial_quote: "", testimonial_author: "",
        extra_info: d.extra_info || "", featured_image: d.featured_image || "",
        photos: d.photos || [], vendors: (d.vendors || []).filter((v) => v.name),
        published: d.published !== false,
      };
      if (cfg.showType) payload.event_type = d.event_type || "";
      await api.put(`/${cfg.coll}/${item.id}`, payload);
      toast.success("Saved & published");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const inp = "input-lux !py-2 text-sm";
  return (
    <div className="bg-white border border-[#E5E5DF] p-5 md:p-6 space-y-6" data-testid={`${cfg.coll}-editor-${item.id}`}>
      <div className="flex items-center justify-between">
        <h4 className="font-serif-lux text-xl">Editing: {d.title || "Untitled"}</h4>
        <button onClick={onClose} className="text-[#8a8a80] hover:text-[#1A1A1A]" aria-label="Close editor"><X size={20} /></button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label={cfg.showType ? "Event title" : "Couple / title"}>
          <input className={inp} value={d.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="pf-title" />
        </Field>
        {cfg.showType && (
          <Field label="Event type / category">
            <input className={inp} value={d.event_type || ""} onChange={(e) => set("event_type", e.target.value)} placeholder="e.g. Birthday, Corporate, Naming" data-testid="pf-type" />
          </Field>
        )}
        <Field label="Date"><input className={inp} value={d.date || ""} onChange={(e) => set("date", e.target.value)} placeholder="e.g. June 2026" /></Field>
        <Field label="Venue"><input className={inp} value={d.venue || ""} onChange={(e) => set("venue", e.target.value)} /></Field>
        <Field label="City / location"><input className={inp} value={d.city || ""} onChange={(e) => set("city", e.target.value)} /></Field>
      </div>

      <Field label="Short preview (shown on the portfolio card)">
        <input className={inp} value={d.preview || ""} onChange={(e) => set("preview", e.target.value)} />
      </Field>
      <Field label="Full description / story">
        <textarea rows={4} className={`${inp} resize-none`} value={d.description || ""} onChange={(e) => set("description", e.target.value)} />
      </Field>

      {/* Featured image */}
      <div>
        <span className="label-lux block mb-2">Featured image</span>
        <div className="flex items-center gap-4">
          <div className="w-28 h-20 rounded-md overflow-hidden bg-[#F1EFE8] flex-shrink-0">
            {d.featured_image && <img src={assetUrl(d.featured_image)} alt="" className="w-full h-full object-cover" />}
          </div>
          <label className="btn-outline-dark !py-2 !px-4 cursor-pointer text-xs inline-flex items-center">
            <Upload size={14} className="mr-1" /> {busy ? "Working…" : "Upload / replace"}
            <input type="file" accept="image/*,video/*" className="hidden" onChange={setFeatured} data-testid="pf-featured-upload" />
          </label>
        </div>
      </div>

      {/* Gallery */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="label-lux">Photo gallery ({(d.photos || []).length})</span>
          <label className="btn-outline-dark !py-2 !px-4 cursor-pointer text-xs inline-flex items-center">
            <Upload size={14} className="mr-1" /> Add photos (multiple)
            <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={addPhotos} data-testid="pf-gallery-upload" />
          </label>
        </div>
        <div className="flex flex-wrap gap-3">
          {(d.photos || []).map((p, i) => (
            <div key={i} className="relative w-24 h-28 rounded-md overflow-hidden bg-[#0E0E0C] group">
              {p.type === "video" ? (
                <video src={assetUrl(p.url)} muted className="w-full h-full object-cover" />
              ) : (
                <img src={assetUrl(p.url)} alt="" className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-black/50 py-1 opacity-0 group-hover:opacity-100 transition">
                <button onClick={() => movePhoto(i, -1)} className="text-white/80 hover:text-white" aria-label="Move left"><ArrowUp size={13} className="-rotate-90" /></button>
                <button onClick={() => movePhoto(i, 1)} className="text-white/80 hover:text-white" aria-label="Move right"><ArrowDown size={13} className="-rotate-90" /></button>
                <button onClick={() => removePhoto(i)} className="text-white/80 hover:text-red-400" aria-label="Remove" data-testid={`pf-photo-del-${i}`}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
          {(d.photos || []).length === 0 && <span className="text-sm text-[#8a8a80]">No photos yet. Upload as many as you like.</span>}
        </div>
      </div>

      {/* Testimonials (link several from the same event — e.g. the bride and the mother of the groom) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="label-lux">Testimonials (add as many as you like for this {cfg.singular.toLowerCase()})</span>
          <button onClick={addTestimonial} className="text-xs text-[#47562F] hover:underline inline-flex items-center gap-1" data-testid="pf-testimonial-add"><Plus size={13} /> Add testimonial</button>
        </div>
        <div className="space-y-4">
          {(d.testimonials || []).map((t, i) => (
            <div key={i} className="border border-[#E5E5DF] rounded-md p-3 space-y-2" data-testid={`pf-testimonial-${i}`}>
              <div className="flex items-start gap-2">
                <textarea rows={2} className={`${inp} resize-none flex-1`} placeholder="Testimonial quote" value={t.quote || ""} onChange={(e) => setTestimonial(i, "quote", e.target.value)} />
                <button onClick={() => removeTestimonial(i)} className="text-[#8a8a80] hover:text-red-600 pt-2" aria-label="Remove testimonial"><Trash2 size={15} /></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input className={inp} placeholder="Author name" value={t.author || ""} onChange={(e) => setTestimonial(i, "author", e.target.value)} />
                <input className={inp} placeholder="Role (e.g. Mother of the Groom)" value={t.role || ""} onChange={(e) => setTestimonial(i, "role", e.target.value)} />
              </div>
            </div>
          ))}
          {(d.testimonials || []).length === 0 && <p className="text-sm text-[#8a8a80]">No testimonials yet. Add one or more — they'll appear grouped together on this {cfg.singular.toLowerCase()}'s page.</p>}
        </div>
      </div>

      {/* Vendors */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="label-lux">Vendors / team</span>
          <button onClick={addVendor} className="text-xs text-[#47562F] hover:underline inline-flex items-center gap-1"><Plus size={13} /> Add vendor</button>
        </div>
        <div className="space-y-2">
          {(d.vendors || []).map((v, i) => (
            <div key={i} className="flex gap-2">
              <input className={`${inp} flex-1`} placeholder="Name" value={v.name || ""} onChange={(e) => setVendor(i, "name", e.target.value)} />
              <input className={`${inp} flex-1`} placeholder="Role (e.g. Florist)" value={v.role || ""} onChange={(e) => setVendor(i, "role", e.target.value)} />
              <button onClick={() => removeVendor(i)} className="text-[#8a8a80] hover:text-red-600 px-2" aria-label="Remove vendor"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </div>

      <Field label="Additional information">
        <textarea rows={3} className={`${inp} resize-none`} value={d.extra_info || ""} onChange={(e) => set("extra_info", e.target.value)} />
      </Field>

      <div className="flex items-center justify-between pt-2 border-t border-[#E5E5DF]">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={d.published !== false} onChange={(e) => set("published", e.target.checked)} data-testid="pf-published" />
          Published (visible on the website)
        </label>
        <button onClick={save} disabled={busy} className="btn-gold !py-2.5 !px-6 text-sm inline-flex items-center" data-testid="pf-save">
          <Save size={15} className="mr-1.5" /> {busy ? "Saving…" : "Save & Publish"}
        </button>
      </div>
    </div>
  );
}

export function PortfolioAdmin({ cfg, content, refresh }) {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [momentBusy, setMomentBusy] = useState(false);

  const load = () => api.get(`/admin/${cfg.coll}`).then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cfg.coll]);

  const create = async () => {
    try {
      const { data } = await api.post(`/${cfg.coll}`, { title: `New ${cfg.singular.toLowerCase()}`, published: false });
      await load();
      setEditingId(data.id);
      toast.success(`${cfg.singular} created — add details, then Save`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm(`Delete this ${cfg.singular.toLowerCase()}? This cannot be undone.`)) return;
    try { await api.delete(`/${cfg.coll}/${id}`); if (editingId === id) setEditingId(null); load(); toast.success("Deleted"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const togglePublish = async (it) => {
    try { await api.put(`/${cfg.coll}/${it.id}`, { published: !(it.published !== false) }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const move = async (i, dir) => {
    const j = i + dir; if (j < 0 || j >= items.length) return;
    const a = items[i], b = items[j];
    try {
      await api.put(`/${cfg.coll}/${a.id}`, { order: j });
      await api.put(`/${cfg.coll}/${b.id}`, { order: i });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setMomentImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setMomentBusy(true);
    try {
      const r = await uploadFile(f);
      await api.put("/content", { [cfg.momentKey]: r.url });
      refresh();
      toast.success("Homepage featured image updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setMomentBusy(false); e.target.value = ""; }
  };

  const momentImg = content?.[cfg.momentKey];

  return (
    <div className="space-y-6" data-testid={`${cfg.coll}-admin`}>
      {/* Homepage "Our Special Moments" featured image */}
      <div className="bg-white border border-[#E5E5DF] p-5 flex items-center gap-4">
        <div className="w-28 h-20 rounded-md overflow-hidden bg-[#F1EFE8] flex-shrink-0">
          {momentImg && <img src={assetUrl(momentImg)} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1">
          <h4 className="font-serif-lux text-lg">Homepage “Our Special Moments” image</h4>
          <p className="text-xs text-[#8a8a80] mb-2">The large image shown for {cfg.kicker} on the homepage.</p>
          <label className="btn-outline-dark !py-2 !px-4 cursor-pointer text-xs inline-flex items-center">
            <Upload size={14} className="mr-1" /> {momentBusy ? "Uploading…" : "Replace image"}
            <input type="file" accept="image/*,video/*" className="hidden" onChange={setMomentImage} data-testid={`${cfg.coll}-moment-upload`} />
          </label>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h3 className="font-serif-lux text-2xl">{cfg.kicker} ({items.length})</h3>
        <button onClick={create} className="btn-gold !py-2.5 !px-6 text-sm inline-flex items-center" data-testid={`${cfg.coll}-add`}>
          <Plus size={15} className="mr-1.5" /> Add {cfg.singular}
        </button>
      </div>

      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={it.id} data-testid={`${cfg.coll}-row-${it.id}`}>
            <div className="bg-white border border-[#E5E5DF] p-4 flex items-center gap-4">
              <div className="w-16 h-16 rounded-md overflow-hidden bg-[#F1EFE8] flex-shrink-0">
                {it.featured_image && <img src={assetUrl(it.featured_image)} alt="" className="w-full h-full object-cover" />}
              </div>
              <button onClick={() => setEditingId(editingId === it.id ? null : it.id)} className="flex-1 text-left">
                <div className="flex items-center gap-2">
                  {editingId === it.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <span className="font-serif-lux text-lg">{it.title || "Untitled"}</span>
                  {it.published === false && <span className="text-[0.6rem] uppercase tracking-[0.14em] bg-[#EDE9DD] text-[#8a7a4a] px-2 py-0.5 rounded">Draft</span>}
                </div>
                <span className="text-xs text-[#8a8a80]">{[cfg.showType ? it.event_type : null, it.city].filter(Boolean).join(" · ")}</span>
              </button>
              <button onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 text-[#8a8a80] hover:text-[#47562F] disabled:opacity-30" aria-label="Move up"><ArrowUp size={16} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="p-1.5 text-[#8a8a80] hover:text-[#47562F] disabled:opacity-30" aria-label="Move down"><ArrowDown size={16} /></button>
              <button onClick={() => togglePublish(it)} className="p-1.5 text-[#8a8a80] hover:text-[#47562F]" aria-label="Toggle publish" data-testid={`${cfg.coll}-toggle-${it.id}`}>
                {it.published === false ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              <button onClick={() => del(it.id)} className="p-1.5 text-[#8a8a80] hover:text-red-600" aria-label="Delete" data-testid={`${cfg.coll}-del-${it.id}`}><Trash2 size={16} /></button>
            </div>
            {editingId === it.id && (
              <div className="mt-2">
                <Editor cfg={cfg} item={it} onSaved={load} onClose={() => setEditingId(null)} />
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-[#8a8a80]">No {cfg.kicker.toLowerCase()} yet. Click “Add {cfg.singular}” to create your first one.</p>}
      </div>
    </div>
  );
}

export const WEDDINGS_ADMIN_CFG = { coll: "weddings", singular: "Wedding", kicker: "Weddings", momentKey: "moments_weddings_image", showType: false };
export const EVENTS_ADMIN_CFG = { coll: "events", singular: "Event", kicker: "Events", momentKey: "moments_events_image", showType: true };
